import { ColumnState, ColDef } from 'ag-grid-community';

/**
 * Extract ordered list of value columns from coldefs
 * @param columns Colstate array
 * @returns list of ordered values
 */
const getOrderedPivotedValueColumn = (columns: ColumnState[]): string[] =>
    columns
        .filter((col) => col.aggFunc && !col.pivot && !col.rowGroup)
        .reduce((acc: string[], value: ColumnState) => {
            if (!acc.includes(value.colId)) acc.push(value.colId);
            return acc;
        }, []);

/**
 * Returns bool whether value coldefs in columns (Coldefs) match provided sort order in orderedValues.
 *
 * @param columns Coldefs array
 * @param orderedValues ordered array of value columns
 * @returns boolean
 */
export const coldefsSortMatches = (
    columns: ColDef[],
    orderedValues: string[],
) =>
    columns
        .filter((col) => orderedValues.includes(col.field))
        .every((col, index) => col.field === orderedValues[index]);

/**
 *  Creates a new array from input columns (Coldefs) and sort its element according to element.field and their order in orderedValues;
 *  Other items order is preserved, any new values column not present in orderedValues is sorted last of value columns.
 *
 * @param columns Coldefs array
 * @param orderedValues ordered array of value columns
 * @returns sorted array of coldefs.
 */
export const updateColdefsOrder = (
    columns: ColDef[],
    orderedValues: string[],
): ColDef[] => {
    const orderIndex = Object.fromEntries(orderedValues.map((v, i) => [v, i]));

    const isNewAggItem = (item: ColDef) =>
        item.aggFunc && !(item.field! in orderIndex);
    const newAggItems = columns?.filter((item) => isNewAggItem(item));

    const sortedElements = columns
        ?.filter((item) => item.field! in orderIndex)
        .sort((a, b) => orderIndex[a.field!] - orderIndex[b.field!]);

    let index = 0;
    const sortedColumns = columns
        .filter((item) => !isNewAggItem(item))
        .map((item) =>
            item.field! in orderIndex ? sortedElements?.[index++] : item,
        );
    //insert new element at after existing columns
    if (newAggItems.length > 0) {
        const insertIndex = sortedColumns.findIndex(
            (col) => col.field === orderedValues[orderedValues.length - 1],
        );
        sortedColumns.splice(insertIndex + 1, 0, ...newAggItems);
    }
    return sortedColumns;
};

/**
 * Returns coldefs ordered as expected in reference COldefs
 *
 * @param columns Coldefs array
 * @param orderedValues ordered array of value columns
 * @returns sorted array of coldefs.
 */
export const getOrderedColdefs = (
    coldefs: ColDef[],
    referenceColdefs: ColDef[] | undefined,
): ColDef[] => {
    if (!referenceColdefs) {
        return coldefs;
    }
    const orderedColumns = getOrderedPivotedValueColumn(
        referenceColdefs as ColumnState[],
    );
    let updatedColdefs = coldefs;
    if (!coldefsSortMatches(coldefs, orderedColumns)) {
        updatedColdefs = updateColdefsOrder(coldefs, orderedColumns);
    }
    return updatedColdefs;
};

/**
 * Returns new Colstate, sorted version of input columns according to orderedValues.
 * Pivoted value columns are re-ordered within pivot groups.
 *
 * @param columns input ColState
 * @param orderedValues order for value columns
 * @returns new ColState
 */
export const updateColStateOrder = (
    columns: ColumnState[],
    orderedValues: string[],
): ColumnState[] => {
    const sortIndex = new Map(orderedValues.map((col, index) => [col, index]));

    //pivot groups or others
    const groups = columns.reduce(
        (acc: Record<string, ColumnState[]>, col) => {
            if (col.aggFunc && orderedValues.includes(col.colId)) {
                acc.aggCols.push(col);
            } else {
                const prefix = col.colId.includes('_')
                    ? col.colId.slice(0, col.colId.lastIndexOf('_'))
                    : null;
                if (prefix) {
                    if (!acc[prefix]) acc[prefix] = [];
                    acc[prefix].push(col);
                } else {
                    acc.others.push(col);
                }
            }
            return acc;
        },
        { others: [], aggCols: [] },
    );

    Object.entries(groups).forEach(([key, group]) => {
        if (key !== 'others' && key !== 'aggCols')
            group.sort((a, b) => {
                const suffixA = a.colId.slice(a.colId.lastIndexOf('_') + 1);
                const suffixB = b.colId.slice(b.colId.lastIndexOf('_') + 1);
                return (
                    (sortIndex.get(suffixA) ?? Infinity) -
                    (sortIndex.get(suffixB) ?? Infinity)
                );
            });
        if (key === 'aggCols')
            group.sort((a, b) => {
                return (
                    (sortIndex.get(a.colId) ?? Infinity) -
                    (sortIndex.get(b.colId) ?? Infinity)
                );
            });
    });

    return ([] as ColumnState[]).concat(...Object.values(groups));
};
